import { Link } from 'react-router-dom';
import { site } from '@shared/site.config';
import { useTitle } from '@/lib/useTitle';
import { useJsonLd } from '@/lib/useJsonLd';
import { faqPageJsonLd } from '@/lib/seo';
import DimensionLine from '@/components/DimensionLine';

export const meta = {
  path: '/industrial-space-for-lease-cambridge-guide',
  title: 'Industrial Space for Lease in Cambridge — Costs, Steps & Tenant Guide',
  description:
    'An answer-first guide to leasing industrial space in Cambridge, Ontario: how availability works, the steps from requirement to signed lease, and the full cost picture beyond base rent — net (NNN) rent, TMI, HST, fixturing and security.',
};

const FAQS = [
  {
    q: 'Do you have a list of available industrial space in Cambridge?',
    a: "No public listings page — for a reason. A list like that is out of date within days, and it tells every other broker in the region exactly what's in play. A direct conversation gets you the real current picture instead.",
  },
  {
    q: 'What lease structures are typical for industrial space?',
    a: "Most industrial leases here are net (NNN) — base rent plus your share of taxes, insurance and common-area costs — though gross and semi-gross terms do come up. I'll walk you through exactly what you're agreeing to before you sign anything.",
  },
  {
    q: 'What does an industrial tenant pay besides base rent?',
    a: 'On a net lease, base rent is only part of the monthly occupancy cost. Expect TMI or additional rent on top — a separate charge for items allocated under the lease, commonly including some combination of taxes, maintenance and insurance — plus HST, and utilities and tenant-specific costs, which sit outside the rent figures.',
  },
  {
    q: 'What should I have ready before asking about space?',
    a: "The size you need, the clear height, the loading you need and your timeline. Industrial tenants may also care heavily about power and highway access. With those facts, the shortlist can be realistic instead of generic.",
  },
  {
    q: 'Do you represent landlords, or only tenants?',
    a: "Both, though never on the same deal at the same time. Whichever side you're on, you get direct representation, not a split-loyalty arrangement.",
  },
  {
    q: 'How fast can I get in front of available space?',
    a: 'Usually the same day. One call gets you a shortlist based on what actually exists right now, including space I know about before it’s formally marketed.',
  },
];

const STEPS = [
  {
    title: 'Set the requirement',
    body: 'Size, clear height, loading, power and timeline. Those five facts decide which Cambridge buildings are even worth a conversation.',
  },
  {
    title: 'Get the current picture',
    body: 'There is no public list to browse — availability turns over too fast. A direct conversation produces a shortlist of what actually exists right now, including space not yet formally marketed.',
  },
  {
    title: 'Compare on total occupancy cost',
    body: 'Face rent alone misleads. Compare each option on base rent plus TMI or additional rent, and model the full term before ranking buildings.',
  },
  {
    title: 'Negotiate the whole deal',
    body: 'Free rent, fixturing periods, tenant-improvement money, security, renewal rights, exclusivity, assignment language and restoration obligations can materially change the economics and risk of a lease.',
  },
  {
    title: 'Review before you sign',
    body: 'Lease structures here are usually net (NNN), but gross and semi-gross terms come up. The signed document governs — walk through exactly what you are agreeing to before you sign anything.',
  },
  {
    title: 'Take possession and fixture',
    body: 'Possession timing and the fixturing period agreed in negotiation set the move-in schedule. The lease terms, not the brochure, decide who does and pays for what.',
  },
];

const COSTS = [
  {
    item: 'Base (net) rent',
    what: 'The quoted rent for the space itself, usually expressed annually per rentable square foot.',
  },
  {
    item: 'TMI / additional rent',
    what: 'A separate estimate or charge for items allocated under the lease, commonly including some combination of taxes, maintenance and insurance.',
  },
  {
    item: 'HST',
    what: 'Quoted rent figures are before HST. Occupancy cost in the lease calculator is shown before HST, utilities and tenant-specific costs.',
  },
  {
    item: 'Utilities & tenant-specific costs',
    what: 'Costs that sit outside the rent figures and vary by operation — they belong in the comparison even though they are not on the listing sheet.',
  },
  {
    item: 'Fixturing & improvements',
    what: 'Free rent, a fixturing period or tenant-improvement money may offset build-out, but only as negotiated. The balance of fit-out is a tenant cost to budget for.',
  },
  {
    item: 'Lease security',
    what: 'Security is one of the business terms reviewed in a lease decision, alongside term, inducements requested and whether the use fits the property and zoning.',
  },
];

const cambridge = site.serviceAreas.find((a) => a.slug === 'cambridge')!;
const leasing = site.services[1];

export default function IndustrialLeasingCambridgeGuide() {
  useTitle(meta.title, meta.description);
  useJsonLd('faq-industrial-leasing-cambridge-guide', faqPageJsonLd(FAQS));

  return (
    <>
      <section className="border-b border-medline bg-grid-sheet">
        <div className="mx-auto max-w-7xl px-5 py-16 sm:px-8 sm:py-24">
          <p className="annot">Tenant guide — Cambridge industrial leasing</p>
          <h1 className="mt-4 max-w-5xl font-display text-[38px] font-semibold leading-[1.03] tracking-tight sm:text-[58px]">
            Industrial space for lease in Cambridge
          </h1>
          <p className="mt-6 max-w-3xl text-[17px] leading-relaxed text-annot">
            Industrial space for lease in Cambridge is found through landlords and their representatives, not a
            public listings portal — availability turns over quickly, so the current picture comes from a direct
            conversation about size, clear height, loading and timing. Most Cambridge industrial leases are net
            (NNN): base rent plus your share of property taxes, insurance and common-area costs, with HST and
            utilities on top. Pinnacle Commercial represents tenants and landlords in Cambridge — never both on
            the same deal — and can usually put a current shortlist together the same day.
          </p>
          <p className="mt-4 font-mono text-[12px] uppercase tracking-wider2 text-annot">Last updated: October 24, 2026</p>
          <div className="mt-9 flex flex-wrap gap-3">
            <Link to="/industrial-space-for-lease-cambridge" className="btn-primary">Cambridge industrial representation</Link>
            <Link to="/commercial-lease-nnn-rent-calculator" className="btn-ghost">Model the occupancy cost</Link>
          </div>
        </div>
      </section>

      <section className="bg-sheet">
        <div className="mx-auto max-w-7xl px-5 py-16 sm:px-8">
          <DimensionLine label="What it involves" className="mb-10" />
          <div className="grid gap-10 lg:grid-cols-[1.25fr_0.75fr]">
            <div className="space-y-5 text-[16px] leading-relaxed text-graphite-600">
              <p>{cambridge.blurb}</p>
              <p>
                The 401/Highway 8 interchange puts most of the city's industrial base within a short haul of the
                rest of the Waterloo Region and the GTA, which is a large part of why tenants tend to stay once
                they're in.
              </p>
              <p>
                Because available space turns over fast, a static list is wrong the day after it's posted. The
                working method is a requirement-first conversation: tell me the size, the clear height, the
                loading you need and the timeline, and you'll get what's realistic in Cambridge right now —
                including space that's about to come available but isn't public yet.
              </p>
            </div>
            <div className="h-fit border-l-2 border-redline bg-redline-100/40 px-6 py-5">
              <p className="font-mono text-[10px] uppercase tracking-wider2 text-redline-600">No listings page, on purpose</p>
              <p className="mt-2 text-[14px] leading-relaxed text-graphite-600">
                A public list would be out of date within days, and it would tell every other broker in the
                region exactly what's in play. The shortlist is built per requirement instead — usually the same
                day you ask.
              </p>
            </div>
          </div>
        </div>
      </section>

      <section className="border-t border-medline">
        <div className="mx-auto max-w-7xl px-5 py-16 sm:px-8">
          <DimensionLine label="The steps, requirement to signed lease" className="mb-10" />
          <div className="grid gap-px border border-medline bg-medline sm:grid-cols-2">
            {STEPS.map((step, i) => (
              <div key={step.title} className="flex gap-5 bg-sheet px-6 py-5">
                <span className="sheet-no pt-0.5">{String(i + 1).padStart(2, '0')}</span>
                <div>
                  <h2 className="font-display text-[19px] leading-snug">{step.title}</h2>
                  <p className="mt-2 text-[15px] leading-relaxed text-graphite-600">{step.body}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="border-t border-medline bg-sheet">
        <div className="mx-auto max-w-7xl px-5 py-16 sm:px-8">
          <DimensionLine label="The full cost picture" className="mb-10" />
          <h2 className="max-w-3xl font-display text-[30px] leading-tight sm:text-[38px]">Rent is only one line of the deal.</h2>
          <p className="mt-4 max-w-3xl text-[15px] leading-relaxed text-annot">
            A tenant comparing Cambridge industrial space should expect these components. Exact figures depend on
            the building and the signed lease — the point is that none of them should arrive as a surprise.
          </p>
          <div className="mt-9 overflow-x-auto border border-medline bg-table">
            <table className="w-full text-left">
              <thead>
                <tr className="border-b border-medline">
                  <th className="px-6 py-4 font-mono text-[11px] uppercase tracking-wider2 text-annot">Cost component</th>
                  <th className="px-6 py-4 font-mono text-[11px] uppercase tracking-wider2 text-annot">What it is</th>
                </tr>
              </thead>
              <tbody>
                {COSTS.map((row) => (
                  <tr key={row.item} className="border-b border-hairline last:border-b-0">
                    <td className="px-6 py-5 font-display text-[17px]">{row.item}</td>
                    <td className="px-6 py-5 text-[15px] leading-relaxed text-graphite-600">{row.what}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p className="mt-5 max-w-3xl text-[14px] leading-relaxed text-annot">
            Run your own numbers with the{' '}
            <Link to="/commercial-lease-nnn-rent-calculator" className="text-redline hover:text-redline-600">
              commercial lease &amp; NNN rent calculator
            </Link>{' '}
            — it converts quoted rent per square foot into monthly and annual occupancy cost, including the effect
            of base-rent-free months.
          </p>
        </div>
      </section>

      <section className="border-t border-medline">
        <div className="mx-auto max-w-7xl px-5 py-16 sm:px-8">
          <DimensionLine label="How Pinnacle Commercial helps" className="mb-10" />
          <div className="grid gap-10 lg:grid-cols-[1.25fr_0.75fr]">
            <div className="space-y-5 text-[16px] leading-relaxed text-graphite-600">
              <p>
                Pinnacle Commercial is a four-city practice — Cambridge, Kitchener, Waterloo and Guelph — not a
                satellite covering Cambridge from somewhere else. The leasing practice covers both sides of the
                table:
              </p>
              <ul className="list-disc space-y-3 pl-5">
                {leasing.points.map((point) => (
                  <li key={point}>{point}</li>
                ))}
              </ul>
              <p>
                Landlords and tenants are both represented, though never on the same deal at the same time — you
                get direct representation, not a split-loyalty arrangement.
              </p>
            </div>
            <div className="h-fit border-l-2 border-redline bg-redline-100/40 px-6 py-5">
              <p className="font-mono text-[10px] uppercase tracking-wider2 text-redline-600">Start here</p>
              <p className="mt-2 text-[14px] leading-relaxed text-graphite-600">
                Send the size, clear height, loading and timeline — or the address of the space you have to
                lease — and the conversation starts from what actually exists right now.
              </p>
              <p className="mt-4">
                <Link to="/contact?interest=leasing" className="btn-primary">Start a conversation</Link>
              </p>
            </div>
          </div>
        </div>
      </section>

      <section className="border-t border-medline bg-table">
        <div className="mx-auto max-w-7xl px-5 py-16 sm:px-8">
          <DimensionLine label="Questions tenants ask" className="mb-10" />
          <div className="max-w-4xl">
            {FAQS.map((f, i) => (
              <div key={f.q} className="grid grid-cols-[auto_1fr] gap-6 border-t border-hairline py-6 last:border-b last:border-hairline">
                <span className="sheet-no pt-1">{String(i + 1).padStart(2, '0')}</span>
                <div>
                  <h2 className="font-display text-[19px] leading-snug">{f.q}</h2>
                  <p className="mt-2 text-[15px] leading-relaxed text-graphite-600">{f.a}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="border-t border-medline bg-sheet">
        <div className="mx-auto max-w-7xl px-5 py-16 sm:px-8">
          <div className="flex flex-wrap items-end justify-between gap-8">
            <div>
              <p className="annot">Cambridge, industrial</p>
              <h2 className="mt-2 max-w-2xl font-display text-[30px] leading-tight">Looking for industrial space in Cambridge — or have space to lease?</h2>
            </div>
            <div className="flex flex-wrap gap-3">
              <Link to="/industrial-space-for-lease-cambridge" className="btn-primary">Industrial leasing — Cambridge</Link>
              <Link to="/leasing" className="btn-ghost">Full leasing services</Link>
              <Link to="/lease-my-commercial-property-waterloo-region-guelph" className="btn-ghost">Lease my commercial property</Link>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}
